// src/pages/VibeDataPrivacyPage.jsx - Privacy & Data
//
// Rewritten 2 October 2026 so every sentence matches the code (review:
// docs/REVIEW_2026-10.md, P0-2 and P0-7). What changed, and why:
//   - "Storage Location: Your Device Only", "100% Encrypted", "0 People With
//     Access" and an invented storage summary ("Self-Talk Entries: 12", from
//     Inner Voice) are gone. The page now says where each kind of data lives.
//   - The Settings tab's switches did nothing: "Cloud Sync" drove a Firestore
//     path that can never be written, and "Anonymous Usage Analytics" was off
//     while Firebase Analytics ran regardless. They are gone rather than kept
//     as decoration. "Import Data" never imported anything; gone too.
//   - "Delete All My Data" deletes what it says (services/DeleteMyDataService.js)
//     and the page says what it does not delete.
//   - The unreachable "Upgrade to Premium... your Inner Voice journey" panels
//     are gone.
//
// Australian English throughout.

import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Lock,
  Cloud,
  Smartphone,
  CheckCircle,
  AlertCircle,
  Download,
  Trash2,
  Eye,
  HardDrive,
  BarChart3,
  MessageCircle,
  Users,
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useApp } from '../context/AppContext';
import { useProgressStore } from '../stores/progressStore';
import { LEGACY_TOOL_KEYS, loadToolData } from '../utils/toolStorage';
import { deleteAllMyData } from '../services/DeleteMyDataService';

const HELP = 'help@divedeepdialogues.com';
const DELETE_REPORT_KEY = 'fv:delete-report';

// What each part of the deletion did, in words. 'none' means there was
// nothing of yours there to delete (for example, no backup for an account made
// on the sign-up page).
const REPORT_LINES = {
  hub: {
    cleared: 'The backed-up copy of your tool writing and Vibe Check results on our servers: deleted.',
    none: 'There was no backed-up copy on our servers for this device to delete.',
    failed: 'We could not reach our servers to delete the backed-up copy.',
  },
  coach: {
    cleared: 'What the Coach remembered about you in this app: deleted.',
    none: 'There was no Coach data to delete from this device.',
    failed: 'We could not reach the Coach to delete what it remembers. You can delete it from the Coach page.',
  },
  progress: {
    cleared: 'Your progress and saved insights in our database: deleted.',
    none: 'There was no progress in our database for this device to delete.',
    failed: 'We could not delete your progress from our database. Please try again, or email us.',
  },
};

// Read once per page load and kept here: the page can mount more than once
// while the app settles after the reload, and the report must survive that.
let deleteReportThisLoad;
const takeDeleteReport = () => {
  if (deleteReportThisLoad !== undefined) return deleteReportThisLoad;
  deleteReportThisLoad = null;
  try {
    const raw = sessionStorage.getItem(DELETE_REPORT_KEY);
    if (raw) {
      sessionStorage.removeItem(DELETE_REPORT_KEY);
      deleteReportThisLoad = JSON.parse(raw);
    }
  } catch { /* no report */ }
  return deleteReportThisLoad;
};

const Row = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-3 rounded-lg bg-white/5 p-4">
    <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-purple-300" aria-hidden="true" />
    <div>
      <h4 className="mb-1 font-semibold text-white">{title}</h4>
      <div className="space-y-2 text-sm leading-relaxed text-slate-300">{children}</div>
    </div>
  </div>
);

const VibeDataPrivacyPage = () => {
  const { exportUserData, assessmentResults } = useApp();
  const completedModules = useProgressStore((s) => s.completedModules) || [];
  const savedInsights = useProgressStore((s) => s.savedInsights) || [];

  // A finished deletion reloads the page (so nothing deleted survives in
  // memory) and leaves its report in sessionStorage for this page to show.
  const [deleteResult, setDeleteResult] = useState(() => takeDeleteReport());
  const [activeTab, setActiveTab] = useState(() => (takeDeleteReport() ? 'management' : 'overview'));
  const [operationStatus, setOperationStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const onThisDevice = useMemo(() => {
    const toolsWithWriting = LEGACY_TOOL_KEYS.filter((key) => loadToolData(key)).length;
    return [
      ['Tools with your writing', toolsWithWriting],
      ['Vibe Check results', assessmentResults ? 'Yes' : 'None'],
      ['Modules finished', completedModules?.length || 0],
      ['Saved insights', savedInsights.length],
    ];
  }, [assessmentResults, completedModules, savedInsights.length]);

  const handleExportData = async () => {
    try {
      setLoading(true);
      const data = await exportUserData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-friendships-vibe-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setOperationStatus({ type: 'success', message: 'Downloaded. Check your downloads folder.' });
    } catch (error) {
      setOperationStatus({ type: 'error', message: 'The download did not work. Please try again.' });
    } finally {
      setLoading(false);
      setTimeout(() => setOperationStatus(null), 4000);
    }
  };

  const handleDeleteEverything = async () => {
    setLoading(true);
    const result = await deleteAllMyData();
    if (result.stoppedEarly) {
      setLoading(false);
      setConfirmingDelete(false);
      setDeleteResult(result);
      return;
    }
    try {
      sessionStorage.setItem(DELETE_REPORT_KEY, JSON.stringify(result));
    } catch { /* the reload still happens; the report is a courtesy */ }
    // A full reload, so nothing that was just deleted lives on in memory and
    // gets written back by the app's own auto-save.
    window.location.replace('/?view=data-privacy');
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-purple-500/30">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Eye className="w-6 h-6 text-purple-400" />
          Where your data is
        </h3>
        <div className="space-y-3">
          <Row icon={Smartphone} title="On this device">
            <p>
              Everything you write in the tools, your Vibe Check results, which modules you have finished, and
              your settings. It is kept in this browser&apos;s normal storage, which is not separately encrypted,
              so anyone who can use this browser could see it. Sign out on shared devices.
            </p>
          </Row>
          <Row icon={Cloud} title="Backed up to our servers">
            <p>
              If you use the app without an account, your tool writing and Vibe Check results are also backed up
              under a random ID, with no name or email address, so they survive clearing your browser. Accounts
              made on the sign-up page are not backed up this way: their tool writing stays on this device only.
            </p>
            <p>
              While you are signed in, your progress and the insights you save from the dashboard are also kept
              in this app&apos;s database. Everything on our servers is encrypted on the way and while stored, on
              Google Cloud, but it is not end-to-end encrypted.
            </p>
          </Row>
          <Row icon={Users} title="Friend Circles">
            <p>
              A circle has nowhere to write. It keeps the name you show it, which of the circle&apos;s modules you
              have finished, and which fixed answer you picked to each question. Once a question reveals, everyone in
              the circle who was asked it sees who picked what. Leaving a circle deletes your answers and your name
              from it.
            </p>
          </Row>
          <Row icon={MessageCircle} title="The Coach">
            <p>
              Your messages, and what the Coach remembers, are stored on our Knowledge Hub. You can see or delete
              them from the Coach page.
            </p>
          </Row>
          <Row icon={BarChart3} title="Usage analytics">
            <p>
              Google&apos;s Firebase Analytics and Performance Monitoring run in this app today. They record which
              screens are opened, when the Vibe Check is started and finished (with its overall score), and how
              quickly pages load. None of it is used
              for advertising, and nothing is sold. Our own standard for apps used by teenagers says Google
              analytics should not run here, and we are removing it.
            </p>
          </Row>
          <Row icon={Lock} title="Who can see it">
            <p>
              In the app, only you, plus your circle for your answers there once a question reveals. Because our servers are not
              end-to-end encrypted, a small number of people at SILVER WOLF Productions could technically read what
              is stored there. We don&apos;t, except to fix a problem you have asked us to fix.
            </p>
          </Row>
        </div>

        <div className="mt-6 pt-6 border-t border-purple-500/20">
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-purple-300" />
            On this device right now
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {onThisDevice.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-2 p-3 bg-white/5 rounded-lg border border-white/10">
                <span className="text-slate-300 text-sm">{label}</span>
                <span className="text-purple-300 font-medium text-sm">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
        <div className="flex items-start gap-4">
          <Shield className="w-8 h-8 text-green-400 mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-xl font-bold text-white mb-3">What we promise</h3>
            <div className="space-y-3 text-slate-300">
              {[
                'We never sell your data, and there are no ads.',
                'We never use your writing to train AI models, or let anyone else.',
                'You can download what you have written, and delete it, on the My Data tab.',
                <>Leaving a circle deletes your answers in it. Deleting your account is by email for now: <a href={`mailto:${HELP}`} className="text-green-300 underline underline-offset-2">{HELP}</a>.</>,
              ].map((line, i) => (
                <p key={i} className="flex items-start gap-3 leading-relaxed">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>{line}</span>
                </p>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const DeleteReport = ({ result }) => {
    if (result.stoppedEarly) {
      return (
        <Card className="p-6 bg-red-500/10 border-red-500/40" role="alert">
          <h3 className="text-lg font-bold text-red-200 mb-2 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Nothing was deleted
          </h3>
          <p className="text-slate-200 text-sm leading-relaxed">
            We could not reach our servers to delete the backed-up copy of your writing. If we had cleared this
            device anyway, the backup would have put it all back the next time you opened the app, so we stopped
            before deleting anything. Check your connection and try again, or email{' '}
            <a href={`mailto:${HELP}`} className="underline underline-offset-2">{HELP}</a>.
          </p>
        </Card>
      );
    }
    const { report } = result;
    const lines = ['hub', 'coach', 'progress']
      .map((part) => REPORT_LINES[part][report[part]])
      .filter(Boolean);
    const remaining = report.device?.remaining || [];
    return (
      <Card className={`p-6 ${result.ok ? 'bg-green-500/10 border-green-500/40' : 'bg-amber-500/10 border-amber-500/40'}`} role="status">
        <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          {result.ok ? <CheckCircle className="w-5 h-5 text-green-400" /> : <AlertCircle className="w-5 h-5 text-amber-300" />}
          {result.ok ? 'Your data has been deleted' : 'Most of your data has been deleted'}
        </h3>
        <ul className="space-y-2 text-sm text-slate-200 list-disc pl-5">
          <li>
            {remaining.length === 0
              ? 'Everything this app kept on this device: deleted.'
              : `Everything this app kept on this device except ${remaining.length} item(s), which this browser would not let us remove. Clearing this site's data in your browser settings will remove them.`}
          </li>
          {lines.map((line) => <li key={line}>{line}</li>)}
        </ul>
        <p className="mt-3 text-sm text-slate-300">
          Not deleted by this button: your account itself, your answers in a Friend Circle (leave the circle to
          delete them), and a copy on another device you opened with a recovery code. Email{' '}
          <a href={`mailto:${HELP}`} className="underline underline-offset-2">{HELP}</a> and we will delete those.
        </p>
      </Card>
    );
  };

  const ManagementTab = () => (
    <div className="space-y-6">
      {deleteResult && <DeleteReport result={deleteResult} />}

      <Card className="p-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Download className="w-6 h-6 text-blue-400" />
          Download My Data
        </h3>
        <p className="text-slate-300 mb-4 leading-relaxed">
          A file with what this device holds for you: everything you have written in the tools, your Vibe Check
          results, your progress, your saved insights and your settings. Keep it somewhere private: it has your
          writing in it.
        </p>
        <Button
          onClick={handleExportData}
          disabled={loading}
          className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
        >
          <Download className="w-4 h-4 mr-2" />
          {loading ? 'Working...' : 'Download My Data'}
        </Button>
      </Card>

      <Card className="p-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/30">
        <h3 className="text-xl font-bold text-red-300 mb-4 flex items-center gap-2">
          <Trash2 className="w-6 h-6" />
          Delete All My Data
        </h3>
        <p className="text-slate-300 mb-2">This deletes, for good:</p>
        <ul className="text-slate-300 text-sm space-y-1 list-disc pl-5 mb-4">
          <li>everything you have written in the tools on this device: grief entries, unsent letters, pain logs, chats, boundaries and the rest</li>
          <li>your Vibe Check results, module progress, saved insights, guide choice and settings on this device</li>
          <li>the backed-up copy of your tool writing and Vibe Check results on our servers, if you use the app without an account</li>
          <li>what the Coach remembers about you in this app</li>
          <li>your progress and saved insights in this app&apos;s database</li>
        </ul>
        <p className="text-slate-300 text-sm mb-4">
          It does not delete your account itself, your answers in a Friend Circle (leave the circle to delete
          them), or a copy on another device you opened with a recovery code (delete it there too). Email{' '}
          <a href={`mailto:${HELP}`} className="text-red-200 underline underline-offset-2">{HELP}</a> for those.
        </p>
        <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20 mb-4">
          <p className="text-red-200 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            This can&apos;t be undone. Download your data first if you want to keep any of it.
          </p>
        </div>

        {!confirmingDelete ? (
          <Button
            variant="danger"
            onClick={() => setConfirmingDelete(true)}
            disabled={loading}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete All My Data
          </Button>
        ) : (
          <div className="p-4 rounded-lg border border-red-500/40 bg-slate-900/60" role="alertdialog" aria-labelledby="confirm-delete-title">
            <p id="confirm-delete-title" className="text-white font-semibold mb-3">
              Delete all of it now?
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="danger"
                onClick={handleDeleteEverything}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Yes, delete everything'}
              </Button>
              <Button variant="secondary" onClick={() => setConfirmingDelete(false)} disabled={loading}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Privacy &amp; Data</h1>
            <p className="text-lg text-slate-300">
              What this app keeps about you, where it keeps it, and how to take it with you or delete it.
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div className="bg-slate-800/50 rounded-xl p-1 flex gap-1 flex-wrap" role="tablist">
              {[
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'management', label: 'My Data', icon: HardDrive },
              ].map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div key="overview" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <OverviewTab />
              </motion.div>
            )}
            {activeTab === 'management' && (
              <motion.div key="management" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <ManagementTab />
              </motion.div>
            )}
          </AnimatePresence>

          {operationStatus && (
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              className={`fixed bottom-6 right-6 left-6 sm:left-auto p-4 rounded-xl shadow-2xl sm:max-w-sm z-50 ${
                operationStatus.type === 'success'
                  ? 'bg-green-900/90 text-green-200 border border-green-500/30'
                  : 'bg-red-900/90 text-red-200 border border-red-500/30'
              }`}
              role="status"
            >
              <div className="flex items-center gap-3">
                {operationStatus.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400" />
                )}
                <span className="font-medium">{operationStatus.message}</span>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VibeDataPrivacyPage;
